import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { JobRequestListByLookupQuery, JobRequestListGlobalQuery, JobRequestListQuery, JobsService } from '../../../services/jobs.service';
import { GridRow } from '@shared/models/grid-row.interface';
import { SearchParameters } from '@shared/models/search-parameters.model';
import { ProjectDetailsComponent } from '@shared/components/project-details/project-details.component';
import { EditJobComponent, EditJobDialogData } from '../../job-details/edit-job/edit-job.component';
import { PROJECT_NODE_TYPE } from '@models/project-node-type.const';

@Component({
  selector: 'app-job-request-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatPaginatorModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, ProjectDetailsComponent],
  templateUrl: './job-request-list.component.html',
  styleUrl: './job-request-list.component.scss'
})
export class JobRequestListComponent implements OnInit {
  protected readonly NODE_TYPE = PROJECT_NODE_TYPE;
  private readonly jobsService = inject(JobsService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly filterChanges = new Subject<string>();

  constructor(private route: ActivatedRoute){

    this.route.queryParams.subscribe(params => {   
      this.key = params['key'];
      this.projectId = params['projectId'] ? Number(params['projectId']) : null;
      this.projectName = params['projectName'];
      this.nodeType = params['nodeType'] ? Number(params['nodeType']) : null;
      this.year = params['year'] ? Number(params['year']) : null;
      this.pageIndex = 0;
      this.updateDisplayedColumns();
      this.loadJobs();
    });
    
  }

  key: string = '';
  year: number | null = null;
  nodeType: number | null = null;
  projectId: number | null = null;
  projectName: string | null = null;

  rows: GridRow[] = [];
  jobRows: GridRow[] = [];
  childMap = new Map<number, GridRow[]>();
  expandedJobIds = new Set<number>();
  queryString = '';
  isLoading = false;
  searchDone = false;
  totalCount = 0;
  pageIndex = 0;
  pageSize = 25;

  displayedColumns: string[] = ['expand', 'name', 'rigName', 'wellTitle', 'lease','description','initiatedOn','sapCode'];
  subDisplayedColumns: string[] = ['name', 'description','initiatedOn','sapCode'];

  ngOnInit(): void {
    this.filterChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadJobs();
      });
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = this.nodeType === PROJECT_NODE_TYPE.LOOKUP || this.nodeType === null
      ? ['expand', 'name', 'projectName', 'rigName', 'wellTitle', 'lease', 'description', 'initiatedOn', 'sapCode']
      : ['expand', 'name', 'rigName', 'wellTitle', 'lease', 'description', 'initiatedOn', 'sapCode'];
  }


  toggleExpand(jobId: number, event: Event): void {
    event.stopPropagation();
    if (this.expandedJobIds.has(jobId)) {
      this.expandedJobIds.delete(jobId);
    } else {
      this.expandedJobIds.add(jobId);
    }
  }

  getChildRows(jobId: number): GridRow[] {
    return this.childMap.get(jobId) ?? [];
  }

  private processRows(): void {
    this.jobRows = this.rows.filter(r => r.type === 'job');
    this.childMap = new Map<number, GridRow[]>();
    for (const row of this.rows) {
      if (row.type === 'jobrequest') {
        if (!this.childMap.has(row.jobId)) {
          this.childMap.set(row.jobId, []);
        }
        this.childMap.get(row.jobId)!.push(row);
      }
    }
  }

  loadJobs(): void {
    this.isLoading = true;
    this.searchDone = false;

    if (this.nodeType === null) {
      const searchParameters: SearchParameters<JobRequestListGlobalQuery> = {
        query: {
          year: this.year,
          filter: this.queryString.trim() || null
        },
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      };

      this.jobsService.getJobsGlobal(searchParameters).subscribe({
        next: result => {
          this.rows = result.items;
          this.processRows();
          this.totalCount = result.totalCount;
          this.pageIndex = result.pageIndex;
          this.pageSize = result.pageSize;
          this.isLoading = false;
          this.searchDone = true;
        },
        error: () => {
          this.rows = [];
          this.jobRows = [];
          this.totalCount = 0;
          this.isLoading = false;
          this.searchDone = true;
        }
      });

      return;
    }

    if (this.nodeType === PROJECT_NODE_TYPE.PROJECT && this.projectId !== null) {
      const searchParameters: SearchParameters<JobRequestListQuery> = {
        query: {
          projectId: this.projectId,
          filter: this.queryString.trim() || null,
          year: this.year
        },
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      };

      this.jobsService.getJobsByProject(searchParameters).subscribe({
        next: result => {
          this.rows = result.items;
          this.processRows();
          this.totalCount = result.totalCount;
          this.pageIndex = result.pageIndex;
          this.pageSize = result.pageSize;
          this.isLoading = false;
          this.searchDone = true;
        },
        error: () => {
          this.rows = [];
          this.jobRows = [];
          this.totalCount = 0;
          this.isLoading = false;
          this.searchDone = true;
        }
      });

      return;
    }

    if (this.nodeType === PROJECT_NODE_TYPE.LOOKUP && this.key) {
      const searchParameters: SearchParameters<JobRequestListByLookupQuery> = {
        query: {
          lookupKey: this.key,
          year: this.year,
          filter: this.queryString.trim() || null
        },
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      };

      this.jobsService.getJobsByLookup(searchParameters).subscribe({
        next: result => {
          this.rows = result.items;
          this.processRows();
          this.totalCount = result.totalCount;
          this.pageIndex = result.pageIndex;
          this.pageSize = result.pageSize;
          this.isLoading = false;
          this.searchDone = true;
        },
        error: () => {
          this.rows = [];
          this.jobRows = [];
          this.totalCount = 0;
          this.isLoading = false;
          this.searchDone = true;
        }
      });

      return;
    }

    this.rows = [];
    this.jobRows = [];
    this.totalCount = 0;
    this.isLoading = false;
    this.searchDone = true;
  }

  onFilterChange(): void {
    this.filterChanges.next(this.queryString.trim().toLowerCase());
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadJobs();
  }

  onRowDoubleClick(row: GridRow): void {
    this.router.navigate([`/jobs`, row.jobId]);
  }

  onAddJob(): void {
    const dialogRef = this.dialog.open<EditJobComponent, EditJobDialogData, boolean>(
      EditJobComponent,
      {
        data: { projectId: this.projectId ?? undefined, year: this.year },
        width: '600px',
        disableClose: true
      }
    );

    dialogRef.afterClosed().subscribe(saved => {
      if (saved) this.loadJobs();
    });
  }
}
